import { useEffect } from 'react';
import { useDocPreviewStore } from '@/stores/docPreview';
import { Icon } from '@/design-system/icons/Icon';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { toast } from '@/stores/toast';

const HPLC_TABLE: Array<[string, string]> = [
  ['CBD', '50.2 mg/mL'],
  ['THC', '< 0.2 mg/mL'],
  ['CBG', '1.4 mg/mL'],
  ['CBC', '0.8 mg/mL'],
  ['Lote', '2026A-#4821'],
  ['Pesticidas', 'não detectado'],
  ['Metais pesados', 'não detectado'],
  ['Solventes residuais', 'não detectado'],
  ['Análise', 'HPLC + GC-MS'],
];

export const DocPreviewModal = () => {
  const open = useDocPreviewStore((s) => s.open);
  const doc = useDocPreviewStore((s) => s.doc);
  const close = useDocPreviewStore((s) => s.close);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open || !doc) return null;

  const rows = doc.rows ?? HPLC_TABLE;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${doc.name}`}
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--canvas)',
          borderRadius: 'var(--r-4)',
          width: 620,
          maxWidth: '100%',
          maxHeight: '90vh',
          boxShadow: 'var(--sh-4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            borderBottom: '1px solid var(--line)',
            background: 'var(--surface)',
          }}
        >
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 'var(--r-2)',
              background: 'var(--primary-soft)',
              color: 'var(--primary-deep)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name={doc.kind === 'pdf' ? 'doc' : 'image'} size={20} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ display: 'block' }}>{doc.name}</strong>
            <small style={{ color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
              {doc.meta ?? `${doc.kind.toUpperCase()} · 1 página`}
            </small>
          </div>
          <Button
            size="sm"
            variant="secondary"
            icon={<Icon name="download" size={14} />}
            onClick={() => toast('Download iniciado', 'download')}
          >
            Baixar
          </Button>
          <button type="button" className="icon-btn" aria-label="Fechar" onClick={close}>
            <Icon name="x" size={16} />
          </button>
        </header>

        <div style={{ overflowY: 'auto', padding: 24, background: 'var(--surface-2)', flex: 1 }}>
          <article
            style={{
              background: '#fff',
              color: '#16201b',
              borderRadius: 'var(--r-3)',
              padding: 28,
              boxShadow: 'var(--sh-2)',
              maxWidth: 540,
              margin: '0 auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #e6e1d2', paddingBottom: 14, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#6b7771' }}>
                  {doc.issuer ?? 'Laboratório verificado · ANVISA'}
                </div>
                <h2 style={{ fontFamily: 'var(--font-serif)', margin: '4px 0 0', fontSize: 22 }}>
                  Laudo HPLC
                </h2>
              </div>
              <Badge tone="success">verificado</Badge>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <tbody>
                {rows.map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid #f0ede3' }}>
                    <td style={{ padding: '8px 0', color: '#6b7771', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {k}
                    </td>
                    <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 500 }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 18, padding: 12, background: '#f0f6f1', border: '1px solid #e3efe6', borderRadius: 8, fontSize: 12, color: '#143d2c' }}>
              Documento assinado digitalmente. Hash SHA-256 disponível mediante solicitação. RDC 327/2019.
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};
